/**
 * OCPP 2.0.1 Display Messages (Part 2 §2.7) — v2.0.1-only feature, no
 * v1.6 equivalent. The CSMS pushes messages for the CP to show on its
 * display; the simulator just stores + lists them (a real CP would render
 * them on an LCD). Domain layer: pure types + a wire→snapshot parser.
 */

export const MESSAGE_PRIORITIES = ['AlwaysFront', 'InFront', 'NormalCycle'] as const;
export type MessagePriority = (typeof MESSAGE_PRIORITIES)[number];

export const MESSAGE_STATES = ['Charging', 'Faulted', 'Idle', 'Unavailable'] as const;
export type MessageState = (typeof MESSAGE_STATES)[number];

export const MESSAGE_FORMATS = ['ASCII', 'HTML', 'URI', 'UTF8'] as const;
export type MessageFormat = (typeof MESSAGE_FORMATS)[number];

export interface MessageContent {
  readonly format: MessageFormat;
  readonly content: string;
  readonly language?: string;
}

export interface DisplayMessageSnapshot {
  readonly id: number;
  readonly priority: MessagePriority;
  readonly message: MessageContent;
  readonly state?: MessageState;
  readonly startDateTime?: string;
  readonly endDateTime?: string;
  /** v2.0.1 carries transactionId as a string. */
  readonly transactionId?: string;
}

function isOneOf<T extends string>(allowed: readonly T[], v: unknown): v is T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v);
}

/**
 * Parse a v2.0.1 `MessageInfoType` wire object into a domain snapshot.
 * Returns null on any shape violation — the handler then replies
 * `Rejected` (SetDisplayMessage) rather than persisting garbage.
 */
export function parseMessageInfo(raw: Record<string, unknown>): DisplayMessageSnapshot | null {
  const id = raw['id'];
  if (typeof id !== 'number' || !Number.isInteger(id) || id < 0) return null;
  if (!isOneOf(MESSAGE_PRIORITIES, raw['priority'])) return null;
  const msg = raw['message'];
  if (typeof msg !== 'object' || msg === null) return null;
  const m = msg as Record<string, unknown>;
  if (!isOneOf(MESSAGE_FORMATS, m['format']) || typeof m['content'] !== 'string') return null;
  const content: MessageContent = {
    format: m['format'],
    content: m['content'],
    ...(typeof m['language'] === 'string' ? { language: m['language'] } : {}),
  };
  const txObj = raw['transactionId'];
  const txStr =
    typeof txObj === 'string' ? txObj : typeof txObj === 'number' ? String(txObj) : undefined;
  return {
    id,
    priority: raw['priority'],
    message: content,
    ...(isOneOf(MESSAGE_STATES, raw['state']) ? { state: raw['state'] } : {}),
    ...(typeof raw['startDateTime'] === 'string' ? { startDateTime: raw['startDateTime'] } : {}),
    ...(typeof raw['endDateTime'] === 'string' ? { endDateTime: raw['endDateTime'] } : {}),
    ...(txStr !== undefined ? { transactionId: txStr } : {}),
  };
}

/** Serialise a snapshot back to the v2.0.1 `MessageInfoType` wire shape. */
export function messageInfoToWire(m: DisplayMessageSnapshot): Record<string, unknown> {
  return {
    id: m.id,
    priority: m.priority,
    message: {
      format: m.message.format,
      content: m.message.content,
      ...(m.message.language !== undefined ? { language: m.message.language } : {}),
    },
    ...(m.state !== undefined ? { state: m.state } : {}),
    ...(m.startDateTime !== undefined ? { startDateTime: m.startDateTime } : {}),
    ...(m.endDateTime !== undefined ? { endDateTime: m.endDateTime } : {}),
    ...(m.transactionId !== undefined ? { transactionId: m.transactionId } : {}),
  };
}
